import { rd, RemoteData } from '@passionware/monads';
import { Resource } from '__types__';
import { ArrowUpRight, Check, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import type { ResourceMembership } from 'v5/core/entity-schema/entitySchema.types';
import { Skeleton } from 'v5/design-system/Skeleton';
import { renderError } from 'v5/patterns/feedback/ErrorAlert';
import {
  EntityCreateAction,
  EntityCreateDialogContent,
  EntityCreateDialogPending,
  EntityCreateDialogShell,
  EntityCreateStep,
  EntityCreateShellContext
} from 'v5/patterns/forms/dialogs/EntityCreateDialog';
import type { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { CreateDraftSummary } from './CreateDraftSummary';
import { resourceCreateDialogLogic } from './resourceCreateDialog.logic';
import { ResourceCreateForm } from './ResourceCreateForm';
import { ResourceDuplicatesFooterConnected } from './ResourceDuplicatesFooter.connected';
import type { ResourceDuplicateCandidate } from './useResourceDuplicates';
import { CreateDraft, ResourceFormServices } from './useResourceFormModel';

export type ResourceCreatedNext = 'close' | 'add-another' | 'go-to-profile';

export interface ResourceCreateDialogProps extends ResourceFormServices {
  /** The `enterprise` flag and the browsing scope decide whether workspaces exist here. */
  services: ResourceFormServices['services'] & WithFeatureService;
  open: boolean;
  onOpenChange(open: boolean): void;
  /** `null` → the first resource type available in the browsing scope. */
  initialResourceTypeId: number | null;
  initialMembership: ResourceMembership;
  /** Side effects only (toast, navigation); the dialog closes or resets itself. */
  onCreated(resource: Resource, next: ResourceCreatedNext): void;
}

/**
 * Create-resource dialog on the generic {@link EntityCreateDialogShell}:
 * step 1 picks the resource type and, with workspaces, the visibility; step 2
 * is the schema form with the recap pinned under the title. Step 1 is skipped
 * when the entry already picked the type and nothing else is left to choose.
 */
export function ResourceCreateDialog({
  open,
  onOpenChange,
  initialResourceTypeId,
  initialMembership,
  onCreated,
  services
}: ResourceCreateDialogProps) {
  return (
    <EntityCreateDialogShell open={open} onOpenChange={onOpenChange}>
      {shell => (
        <ResourceCreateDialogContent
          initialResourceTypeId={initialResourceTypeId}
          initialMembership={initialMembership}
          onCreated={onCreated}
          services={services}
          {...shell}
        />
      )}
    </EntityCreateDialogShell>
  );
}

type ResourceCreateDialogContentProps = Omit<
  ResourceCreateDialogProps,
  'open' | 'onOpenChange'
> &
  EntityCreateShellContext;

/** Waits for what decides the steps; the wizard keeps its first step for good. */
function ResourceCreateDialogContent(props: ResourceCreateDialogContentProps) {
  const { services, initialResourceTypeId } = props;
  const { t } = useTranslation('resources', {
    keyPrefix: 'ResourceCreateDialog'
  });
  const scopeRd = services.appScopeService.useResolvedAppScope();
  const resourceTypesRd = services.resourceTypeService.useResourceTypes(
    rd.tryMap(scopeRd, appScopeUtils.identityFromResolved)
  );
  const enterpriseRd = services.featureService.useFeature(
    services.appScopeService.useOrganizationScopeIdentity(),
    'enterprise'
  );
  // A flag that fails to load keeps today's flow: visibility stays a choice.
  const isEnterpriseRd = rd.isError(enterpriseRd) ? rd.of(true) : enterpriseRd;

  return rd
    .journey(
      rd.combine({
        resourceTypes: resourceTypesRd,
        isEnterprise: isEnterpriseRd,
        browsingOrganization: rd.map(
          scopeRd,
          resolved =>
            appScopeUtils.identityFromResolved(resolved).level ===
            'organization'
        )
      })
    )
    .wait(
      <EntityCreateDialogPending
        title={t('title')}
        description={t('description')}
      >
        <Skeleton className="h-64 w-full" />
      </EntityCreateDialogPending>
    )
    .catch(error => (
      <EntityCreateDialogPending
        title={t('title')}
        description={t('description')}
      >
        {renderError(error)}
      </EntityCreateDialogPending>
    ))
    .map(({ resourceTypes, isEnterprise, browsingOrganization }) => {
      // As the resource pane: only the organisation URL without enterprise
      // hides the workspaces; a workspace URL keeps them.
      const workspaceSections = isEnterprise || !browsingOrganization;
      return (
        <ResourceCreateWizard
          {...props}
          workspaceSections={workspaceSections}
          initialStep={
            resourceCreateDialogLogic.opensOnSetup({
              hasVisibilityChoice: workspaceSections,
              resourceTypeCount: resourceTypes.length,
              typeGiven: initialResourceTypeId !== null
            })
              ? 'setup'
              : 'form'
          }
          canGoBack={resourceCreateDialogLogic.canGoBack({
            hasVisibilityChoice: workspaceSections,
            resourceTypeCount: resourceTypes.length
          })}
        />
      );
    });
}

function ResourceCreateWizard({
  initialResourceTypeId,
  initialMembership,
  onCreated,
  services,
  workspaceSections,
  initialStep,
  canGoBack,
  onDirtyChange,
  close
}: ResourceCreateDialogContentProps & {
  /** See `ResourceCreateForm`; the membership still follows the entry's browsing scope. */
  workspaceSections: boolean;
  initialStep: EntityCreateStep;
  canGoBack: boolean;
}) {
  const { t } = useTranslation('resources', {
    keyPrefix: 'ResourceCreateDialog'
  });
  const scopeRd = services.appScopeService.useResolvedAppScope();
  const identity = rd.tryMap(scopeRd, appScopeUtils.identityFromResolved);
  const organizationId = rd.tryMap(scopeRd, scope => scope.organizationId);
  const resourceTypesRd =
    services.resourceTypeService.useResourceTypes(identity);
  const workspacesRd = services.enterpriseService.useWorkspaces(
    organizationId,
    'active'
  );
  const resourceTypeIdRd = rd.map(resourceTypesRd, types =>
    initialResourceTypeId !== null
      ? initialResourceTypeId
      : (types[0]?.id ?? null)
  );

  // Step-1 choices live here: the form edits them and the pinned recap bar
  // shows them. Until the user touches them they derive from the props.
  const [draftEdits, setDraftEdits] = useState<CreateDraft | null>(null);
  const draftRd: RemoteData<CreateDraft | null> = rd.map(
    resourceTypeIdRd,
    resourceTypeId =>
      draftEdits ??
      (resourceTypeId === null
        ? null
        : {
            resourceTypeId,
            profilePicture: null,
            membership: initialMembership
          })
  );
  const draft = rd.mapOrElse(draftRd, value => value, null);
  // Typed in the form, shown by the duplicate warning in the footer.
  const [typedName, setTypedName] = useState('');
  const [duplicateCandidate, setDuplicateCandidate] =
    useState<ResourceDuplicateCandidate | null>(null);
  const actions: EntityCreateAction<ResourceCreatedNext>[] = [
    {
      id: 'add-another',
      label: t('actions.add-another'),
      icon: <Plus />,
      after: 'reset'
    },
    {
      id: 'go-to-profile',
      label: t('actions.go-to-profile'),
      icon: <ArrowUpRight />,
      after: 'close'
    },
    {
      id: 'close',
      label: t('actions.close'),
      icon: <Check />,
      primary: true,
      after: 'close'
    }
  ];

  return (
    <EntityCreateDialogContent<Resource, ResourceCreatedNext>
      title={{
        setup: workspaceSections ? t('titleTypeAndVisibility') : t('titleType'),
        form: canGoBack ? t('titleDetails') : t('title')
      }}
      description={t('description')}
      initialStep={initialStep}
      canGoBack={canGoBack}
      canProceed={
        // "Specific workspaces" with none picked would create it hidden.
        draft !== null &&
        !(
          draft.membership.type === 'workspaces' &&
          draft.membership.workspaceIds.length === 0
        )
      }
      actions={actions}
      onCreated={(created, next) => {
        // The next resource keeps type and visibility, not this one's face.
        if (next === 'add-another') {
          setDraftEdits(edits => edits && { ...edits, profilePicture: null });
          setTypedName('');
          setDuplicateCandidate(null);
        }
        onCreated(created, next);
      }}
      errorFallback={t('createFailed')}
      onDirtyChange={onDirtyChange}
      close={close}
      renderSummary={({ onEdit, canEdit }) =>
        draft && (
          <CreateDraftSummary
            draft={draft}
            resourceTypes={resourceTypesRd}
            workspaces={workspacesRd}
            showVisibility={workspaceSections}
            onEdit={onEdit}
            editDisabled={!canEdit}
          />
        )
      }
      renderFooterNotice={({ formKey }) =>
        rd.tryMap(scopeRd, scope => (
          <ResourceDuplicatesFooterConnected
            key={formKey}
            typedName={typedName}
            candidate={duplicateCandidate}
            organizationId={scope.organizationId}
            workspaces={rd.tryGet(workspacesRd)}
            services={services}
          />
        ))
      }
      renderForm={({ step, formKey, formRef, onStateChange, requestSubmit }) =>
        // The body is not padded (the form spans it); these pad themselves.
        rd
          .journey(draftRd)
          .wait(
            <div className="px-6 py-3">
              <Skeleton className="h-64 w-full" />
            </div>
          )
          .catch(error => <div className="px-6 py-3">{renderError(error)}</div>)
          .map(draft =>
            draft === null ? (
              <p className="px-6 py-3 text-fg-muted">{t('noResourceTypes')}</p>
            ) : (
              <ResourceCreateForm
                key={formKey}
                ref={formRef}
                step={step}
                draft={draft}
                onDraftChange={update =>
                  setDraftEdits(edits => update(edits ?? draft))
                }
                onStateChange={onStateChange}
                onSubmitRequest={requestSubmit}
                onNameChange={setTypedName}
                onDuplicateCandidateChange={setDuplicateCandidate}
                workspaceSections={workspaceSections}
                services={services}
              />
            )
          )
      }
    />
  );
}
